import { adaptSession } from '@/entities/session/api/sessionAdapter'
import { LoginCodeError, type LoginCodeChallenge } from '@/entities/session/model/loginChallenge'
import type { Session } from '@/entities/session/model/session'
import { accessTokenStore } from '@/shared/api/accessTokenStore'
import { publicApiClient, refreshAccessToken } from '@/shared/api/apiClient'
import { extractAccessToken } from '@/shared/api/extractAccessToken'
import { authLogin } from '@/shared/api/generated/clients/authLogin'
import { authLoginResendCode } from '@/shared/api/generated/clients/authLoginResendCode'
import { authLoginVerifyCode } from '@/shared/api/generated/clients/authLoginVerifyCode'
import { authLogout } from '@/shared/api/generated/clients/authLogout'
import { authPasswordReset } from '@/shared/api/generated/clients/authPasswordReset'
import { authSupportStop } from '@/shared/api/generated/clients/authSupportStop'
import { authSwitch } from '@/shared/api/generated/clients/authSwitch'
import { edyAllUsersLoginAs } from '@/shared/api/generated/clients/edyAllUsersLoginAs'
import { invitesAccept } from '@/shared/api/generated/clients/invitesAccept'
import { meAcceptInvite } from '@/shared/api/generated/clients/meAcceptInvite'
import { meGetMe } from '@/shared/api/generated/clients/meGetMe'
import { meGetRoles } from '@/shared/api/generated/clients/meGetRoles'
import { meReadEvent } from '@/shared/api/generated/clients/meReadEvent'
import { paymentsProcess } from '@/shared/api/generated/clients/paymentsProcess'
import type { AcceptInviteRequestV4 } from '@/shared/api/generated/models/AcceptInviteRequestV4'
import type { MeChangePasswordStatus200 } from '@/shared/api/generated/models/MeChangePassword'
import type { MyInternshipV4 } from '@/shared/api/generated/models/MyInternshipV4'
import type { MyRoleV4 } from '@/shared/api/generated/models/MyRoleV4'
import type { ReadEventResponseV4 } from '@/shared/api/generated/models/ReadEventResponseV4'
import {
  cancelLanguagePersistence,
  getLanguageVersion,
  syncLanguageFromServer,
} from '@/shared/lib/language/languagePreference'

const getRoleContexts = async () => {
  const { data } = await meGetRoles()
  return data.result
}

const hydrateSession = async (authPayload: unknown, languageVersion = getLanguageVersion()) => {
  const initialSession = adaptSession(authPayload)
  const [mePayload, roleContexts] = await Promise.all([
    initialSession.pages.length > 0
      ? Promise.resolve(undefined)
      : meGetMe().then(({ data }) => data),
    getRoleContexts(),
  ])

  const session = adaptSession(authPayload, mePayload, roleContexts)
  syncLanguageFromServer(session.user.language, session.user.email, languageVersion)
  return session
}

const hydrateSessionWithOneRetry = async (
  authPayload: unknown,
  languageVersion = getLanguageVersion()
) => {
  try {
    return await hydrateSession(authPayload, languageVersion)
  } catch {
    return hydrateSession(authPayload, languageVersion)
  }
}

const setTransitionAccessToken = (payload: unknown, missingTokenMessage: string) => {
  const token = extractAccessToken(payload)
  if (!token) {
    throw new Error(missingTokenMessage)
  }
  accessTokenStore.set(token)
}

const hydrateAuthTransition = async (
  payload: unknown,
  missingTokenMessage: string,
  languageVersion = getLanguageVersion()
) => {
  setTransitionAccessToken(payload, missingTokenMessage)
  try {
    return await hydrateSession(payload, languageVersion)
  } catch (error) {
    accessTokenStore.clear()
    throw error
  }
}

const hydrateSupportAuthTransition = async (payload: unknown, missingTokenMessage: string) => {
  cancelLanguagePersistence()
  setTransitionAccessToken(payload, missingTokenMessage)
  try {
    return await hydrateSessionWithOneRetry(payload)
  } catch {
    const session = adaptSession(payload)
    syncLanguageFromServer(session.user.language, session.user.email, getLanguageVersion())
    return session
  }
}

export const restoreSession = async () => {
  const languageVersion = getLanguageVersion()
  const authPayload = await refreshAccessToken()
  return hydrateSession(authPayload, languageVersion)
}

export type LoginResult =
  | { kind: 'authenticated'; session: Session }
  | { kind: 'password-change-required' }
  | { kind: 'terms-acceptance-required'; payload: unknown }

export type SignInResult = LoginResult | { kind: 'code-required'; challenge: LoginCodeChallenge }

const completeLogin = async (
  payload: { redirectAction?: unknown },
  missingTokenMessage: string,
  languageVersion = getLanguageVersion()
): Promise<LoginResult> => {
  if (payload.redirectAction === 'ChangePassword') {
    setTransitionAccessToken(payload, missingTokenMessage)
    return { kind: 'password-change-required' }
  }
  if (payload.redirectAction === 'AcceptTerms') {
    setTransitionAccessToken(payload, missingTokenMessage)
    return { kind: 'terms-acceptance-required', payload }
  }
  return {
    kind: 'authenticated',
    session: await hydrateAuthTransition(payload, missingTokenMessage, languageVersion),
  }
}

export const signIn = async (email: string, password: string): Promise<SignInResult> => {
  const languageVersion = getLanguageVersion()
  const { data } = await authLogin({
    body: { email, password },
    contentType: 'application/json',
    client: publicApiClient,
  })
  if (!data.ok) {
    throw new Error(data.errorMsg || 'Login failed')
  }
  if (data.result.codeRequired) {
    if (!data.result.pendingToken) {
      throw new Error('Login response did not contain a pending token')
    }
    return {
      kind: 'code-required',
      challenge: {
        pendingToken: data.result.pendingToken,
        codeSentTo: data.result.codeSentTo ?? '',
      },
    }
  }
  return completeLogin(data, 'Login response did not contain an access token', languageVersion)
}

export const verifyLoginCode = async (
  pendingToken: string,
  code: string,
  rememberDevice: boolean
) => {
  const languageVersion = getLanguageVersion()
  const { data } = await authLoginVerifyCode({
    body: { pendingToken, code: code.trim(), rememberDevice },
    contentType: 'application/json',
    client: publicApiClient,
  })
  if (!data.ok) {
    throw new LoginCodeError(data.errorMsg || 'Code verification failed')
  }
  return completeLogin(
    data,
    'Code verification response did not contain an access token',
    languageVersion
  )
}

export const completePasswordChange = (payload: MeChangePasswordStatus200) =>
  completeLogin(payload, 'Password change response did not contain an access token')

export const completeTermsAcceptance = (loginPayload: unknown) => hydrateSession(loginPayload)

export const resendLoginCode = async (pendingToken: string) => {
  const { data } = await authLoginResendCode({
    body: { pendingToken },
    contentType: 'application/json',
    client: publicApiClient,
  })
  if (!data.ok) {
    throw new LoginCodeError(data.errorMsg || 'Code resend failed')
  }
}

export const startSupportSession = async (userId: string, caseNumber: string): Promise<Session> => {
  const { data } = await edyAllUsersLoginAs({
    path: { id: userId },
    body: { supportCaseNr: caseNumber },
    contentType: 'application/json',
  })

  return hydrateSupportAuthTransition(
    data,
    'Support Session response did not contain an access token'
  )
}

export const stopSupportSession = async (): Promise<Session> => {
  const { data } = await authSupportStop()
  return hydrateSupportAuthTransition(
    data,
    'Support Session return response did not contain an access token'
  )
}

const switchContext = async (contextId: string | null) => {
  const languageVersion = getLanguageVersion()
  const { data } = await authSwitch({
    body: contextId ? { orgId: contextId } : {},
  })
  const token = extractAccessToken(data)
  if (!token) {
    throw new Error('Role switch response did not contain an access token')
  }
  accessTokenStore.set(token)
  return hydrateSession(data, languageVersion)
}

export const switchRoleContext = (context: MyRoleV4) => switchContext(context.orgId)

export const switchInternshipContext = (internship: MyInternshipV4) =>
  switchContext(internship.internId)

export const acceptOrgInvite = async (inviteId: string) => {
  const languageVersion = getLanguageVersion()
  const { data } = await meAcceptInvite({
    body: { inviteId },
    contentType: 'application/json',
  })
  const token = extractAccessToken(data)
  if (!token) {
    throw new Error('Invite acceptance response did not contain an access token')
  }
  accessTokenStore.set(token)
  return hydrateSession(data, languageVersion)
}

export const completePayment = async (orderReference: string, paymentReference: string) => {
  const { data } = await paymentsProcess({
    body: { orderReference, paymentReference },
    contentType: 'application/json',
  })
  return {
    orderType: data.result.orderType,
    session: await switchContext(data.result.switchToOrgId),
  }
}

export type NotificationEventTransition = {
  result: ReadEventResponseV4
  session?: Session
}

export const openNotificationEvent = async (
  eventId: string
): Promise<NotificationEventTransition> => {
  const languageVersion = getLanguageVersion()
  const { data } = await meReadEvent({
    body: { eventId },
    contentType: 'application/json',
  })

  const result = data.result
  const token = extractAccessToken(data)
  if (result.orgChanged && !token) {
    throw new Error('Notification context switch did not contain an access token')
  }
  if (!token) {
    if (result.user) {
      syncLanguageFromServer(result.user.lang, result.user.email ?? 'current-user', languageVersion)
    }
    return { result }
  }

  accessTokenStore.set(token)
  return { result, session: await hydrateSession(data, languageVersion) }
}

export const signOut = async () => {
  cancelLanguagePersistence()
  try {
    await authLogout()
  } finally {
    accessTokenStore.clear()
  }
}

export const resetPassword = async (token: string, newPassword: string) => {
  const languageVersion = getLanguageVersion()
  const { data } = await authPasswordReset({
    body: { token, newPassword },
    contentType: 'application/json',
  })
  return hydrateAuthTransition(
    data,
    'Password reset response did not contain an access token',
    languageVersion
  )
}

export const acceptInvite = async (body: AcceptInviteRequestV4) => {
  const languageVersion = getLanguageVersion()
  const { data } = await invitesAccept({
    body,
    contentType: 'application/json',
    client: publicApiClient,
  })
  return {
    roleGranted: data.result.roleGranted,
    session: await hydrateAuthTransition(
      data,
      'Invite acceptance response did not contain an access token',
      languageVersion
    ),
  }
}
